import { Engine } from './core/engine.js';
import { createConfig } from './core/config.js';

import { RenderSystem } from './render/index.js';
import { MaterialSystem } from './materials/index.js';
import { SkySystem } from './sky/index.js';
import { WorldSystem } from './world/index.js';
import { PhysicsSystem } from './physics/index.js';
import { PlayerSystem } from './player/index.js';
import { WeaponSystem } from './weapons/index.js';
import { FxSystem } from './fx/index.js';
import { AiSystem } from './ai/index.js';
import { UiSystem } from './ui/index.js';
import { AudioSystem } from './audio/index.js';
import { GameSystem } from './game/index.js';

import { installShotApi } from './dev/shots.js';
import { prewarm } from './core/prewarm.js';

const params = new URLSearchParams(location.search);
const capture = params.get('capture') === '1';
// Deterministic shutter for the pixel gate: the engine does not schedule its own
// frames, the driver advances exactly N of them through window.__PUMP__. Opt-in,
// because tools that measure real frame pacing (tools/perf.mjs) need the loop to
// free-run. See the long comment in src/dev/shots.js.
const lockstep = capture && params.get('lockstep') === '1';

// Default quality is 'potato': the game was tuned on an M-series laptop and
// anything higher is a gamble on unknown hardware. Fastest boot, highest fps;
// players opt UP via ?q= or the ESC menu (their choice is remembered).
let savedQuality = null;
try { savedQuality = localStorage.getItem('ow-quality'); } catch {}
if (!['potato', 'low', 'medium', 'high', 'ultra'].includes(savedQuality)) savedQuality = null;

const config = createConfig({
  quality: params.get('q') ?? (capture ? 'ultra' : savedQuality ?? 'potato'),
  deterministic: capture,
});

const canvas = document.getElementById('game');

const engine = new Engine({ canvas, config });

// Registration order is irrelevant — Registry topo-sorts on static deps.
engine
  .add(RenderSystem)
  .add(MaterialSystem)
  .add(SkySystem)
  .add(WorldSystem)
  .add(PhysicsSystem)
  .add(PlayerSystem)
  .add(WeaponSystem)
  .add(FxSystem)
  .add(AiSystem)
  .add(UiSystem)
  .add(AudioSystem)
  .add(GameSystem);

// Boot screen (static markup in index.html so it paints instantly). Removed
// outright in capture mode so the pixel harness never sees a single px of it.
const bootEl = document.getElementById('boot');
const bootStage = document.getElementById('boot-stage');
const bootBar = document.getElementById('boot-bar');
if (capture) bootEl?.remove();
const STAGE_LABELS = {
  render: 'IGNITING RENDERER', materials: 'FORGING MATERIALS', sky: 'MIXING ATMOSPHERE',
  world: 'BUILDING THE STREET', physics: 'SOLVING THE WORLD', player: 'LACING BOOTS',
  weapons: 'MACHINING WEAPONS', fx: 'PRIMING PYROTECHNICS', ai: 'RAISING THE GARRISON',
  ui: 'PAINTING THE HUD', audio: 'TUNING THE SOUNDSCAPE', game: 'STARTING THE MATCH',
};
const bootProgress = capture
  ? undefined
  : (id, i, total) => {
      if (bootStage) bootStage.textContent = STAGE_LABELS[id] ?? id.toUpperCase();
      // +1 leaves the last stretch of the bar for the shader pre-warm below.
      if (bootBar) bootBar.style.width = `${((i / (total + 1)) * 100).toFixed(1)}%`;
    };

try {
  await engine.init(bootProgress);
} catch (err) {
  bootEl?.remove();
  console.error('[boot] init failed', err);
  document.body.insertAdjacentHTML(
    'beforeend',
    `<pre style="position:fixed;inset:0;padding:2rem;color:#f66;background:#000;
       font:12px/1.5 ui-monospace,monospace;overflow:auto;z-index:9999;white-space:pre-wrap">
BOOT FAILURE\n\n${err.stack ?? err.message}</pre>`
  );
  throw err;
}

const shotApi = installShotApi(engine, { capture, lockstep });

// Compile every shader permutation before the frame loop starts. Measured: without
// this, 86 programs compile lazily during play, up to 30 on one frame, producing
// 3.1-3.9 SECOND stalls. See src/core/prewarm.js.
//
// ON BY DEFAULT since the capture path was made frame-deterministic; opt out with
// `?prewarm=0`. It is now PROVEN pixel-neutral: `tools/baseline.mjs` with
// `--query=prewarm=0` vs `--query=prewarm=1` reports identical:true on all 11
// shots (0 changed pixels, maxDelta 0). The two things that previously made the
// ~1.4 s pre-warm spend look like a visual change were both boot-duration
// couplings OUTSIDE the subsystems: (1) the shutter frame index was latency-bound
// because the engine kept stepping through the driver's round trips — fixed by
// lockstep in src/dev/shots.js; (2) `will-change: transform` on the compass strip
// cached a composited-layer raster taken at a wall-clock-dependent moment — fixed
// in src/ui/style.js.
if (!capture && bootStage) {
  bootStage.textContent = 'COMPILING SHADERS — THE LONG ONE';
  if (bootBar) bootBar.style.width = '94%';
  await new Promise((r) => requestAnimationFrame(r));
}
const warmup = params.get('prewarm') === '0' ? { ok: false, reason: 'disabled by ?prewarm=0' } : await prewarm(engine);
console.info('[boot] prewarm', warmup);
window.__PREWARM__ = warmup;

engine.start();

if (!capture && bootEl) {
  if (bootBar) bootBar.style.width = '100%';
  bootEl.classList.add('done'); // fades via CSS…
  setTimeout(() => bootEl.remove(), 600); // …then leaves the DOM entirely
}

// Remember the ESC-menu quality choice across visits.
engine.ctx.events.on('ui:quality', (e) => {
  try { localStorage.setItem('ow-quality', e.quality); } catch {}
});

// Capture harness handshake: only flag ready once a frame has actually landed.
//
// BOOT_FRAMES is deliberately a frame COUNT, not a rAF race. In lockstep mode the
// engine has no loop of its own, so we hand-pump exactly this many frames and only
// then raise __READY__; the shot is therefore always applied at engine frame 3, no
// matter how long boot (or pre-warm) took in wall-clock terms.
const BOOT_FRAMES = 3;
if (lockstep) {
  await shotApi.pump(BOOT_FRAMES);
  window.__READY__ = true;
} else {
  let warm = 0;
  const readyProbe = () => {
    if (++warm >= BOOT_FRAMES) {
      window.__READY__ = true;
      return;
    }
    requestAnimationFrame(readyProbe);
  };
  requestAnimationFrame(readyProbe);
}

window.__ENGINE__ = engine;

if (import.meta.hot) {
  import.meta.hot.dispose(() => engine.dispose());
}
